"use client";

import { useMemo, useState } from "react";
import { HighlightedText } from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  collapseWhitespace,
  countWords,
} from "@/lib/tool-kit/text-analysis/segment";
import {
  MAX_SUMMARY_SENTENCES,
  type Summary,
  type SummaryMethod,
  summarize,
} from "@/lib/tool-kit/text-analysis/summarize";

/** English strings. A translated content file overrides these through `ui`. */
export const TEXT_SUMMARIZER_STRINGS = {
  textLabel: "Text to summarize",
  textPlaceholder:
    "Paste an article, a chapter, or your own draft. Several paragraphs give the scoring the most to work with.",
  trySample: "Try a sample",
  clear: "Clear",
  methodLabel: "Scoring method",
  methodTextrank: "TextRank (sentence graph)",
  methodFrequency: "Word frequency",
  lengthLabel: "Summary length",
  bySentences: "Number of sentences",
  byPercent: "Percentage of sentences",
  sentencesSlider: "one:Keep {count} sentence|other:Keep {count} sentences",
  percentSlider: "Keep {percent} of the sentences",
  leadBias: "Give extra weight to the first sentence of each paragraph",
  summarize: "Summarize",
  emptyPrompt:
    "Paste a text and choose Summarize. Nothing leaves your browser.",
  tooShort:
    "Add more text: the summarizer needs at least three sentences to choose from.",
  changed:
    "The text has changed since the last summary. Choose Summarize to update it.",
  truncated:
    "This text is long, so only its first {count} sentences were scored.",
  results: "Summary",
  statSentences: "Sentences kept",
  statWords: "Words in the summary",
  statShare: "Of the original length",
  copySummary: "Copy summary",
  copied: "Copied!",
  honesty:
    "Every sentence above is copied word for word from the source and chosen by score — nothing is rewritten, so check that the summary reads sensibly on its own, and cite the source if you use it.",
  keywordsHeading: "Top keywords",
  keywordsNone: "No word appears often enough to rank.",
  highlightHeading: "Where the chosen sentences sit in the original",
  highlightLabel: "The original text with the chosen sentences highlighted",
  rankingHeading: "How the sentences scored",
  rankingHint:
    "Scores are relative: the best sentence is 100. Only the top {count} are shown.",
  colRank: "Rank",
  colSentence: "Sentence",
  colScore: "Score",
  colKept: "In summary",
  kept: "Kept",
  sample:
    "Memory consolidation is the process by which new, fragile memories become stable long-term memories. For more than a century, researchers have asked why it happens mostly during sleep.\n\nDuring deep sleep, the brain replays patterns of activity from the day. Recordings from the hippocampus show the same sequences firing again, only faster. This replay is thought to move memories from the hippocampus into the cortex for long-term storage.\n\nSleep also prunes. Connections that were strengthened during the day are scaled back overnight, which may keep the brain from saturating and leave room for new learning. The weather on the night of a study rarely matters.\n\nFor students, the practical lesson is simple. Sleep after studying protects what was learned, and a night without sleep before an exam undoes much of the benefit of the revision that preceded it.",
} as const;

type Strings = typeof TEXT_SUMMARIZER_STRINGS;
type LengthMode = "sentences" | "percent";

interface Settings {
  method: SummaryMethod;
  mode: LengthMode;
  sentences: number;
  percent: number;
  leadBias: boolean;
}

const MIN_SENTENCES = 3;
const RANKING_ROWS = 10;
const EXCERPT_LENGTH = 110;

function excerpt(text: string): string {
  const flat = collapseWhitespace(text);
  return flat.length > EXCERPT_LENGTH
    ? `${flat.slice(0, EXCERPT_LENGTH)}…`
    : flat;
}

function Keywords({
  summary,
  strings,
}: {
  summary: Summary;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.keywordsHeading}</h3>
      {summary.keywords.length === 0 ? (
        <p className={HINT}>{strings.keywordsNone}</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {summary.keywords.map((keyword) => (
            <li
              className="rounded-md border border-border bg-card px-3 py-1 text-foreground text-sm"
              key={keyword.word}
            >
              {keyword.word}{" "}
              <span className="text-muted-foreground text-xs">
                {fmt(keyword.count)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Ranking({
  text,
  summary,
  strings,
}: {
  text: string;
  summary: Summary;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const ranked = summary.sentences
    .filter((sentence) => !sentence.heading)
    .sort((a, b) => b.score - a.score || a.start - b.start)
    .slice(0, RANKING_ROWS);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.rankingHeading}</h3>
      <p className={HINT}>
        {t(strings.rankingHint, { count: fmt(ranked.length) })}
      </p>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.colRank}
              </th>
              <th className={TH} scope="col">
                {strings.colSentence}
              </th>
              <th className={TH} scope="col">
                {strings.colScore}
              </th>
              <th className={TH} scope="col">
                {strings.colKept}
              </th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((sentence, index) => (
              <tr key={sentence.start}>
                <td className={TD}>{fmt(index + 1)}</td>
                <td className={TD}>
                  {excerpt(text.slice(sentence.start, sentence.end))}
                </td>
                <td className={TD}>{fmt(Math.round(sentence.score * 100))}</td>
                <td className={TD}>{sentence.selected ? strings.kept : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Results({
  text,
  summary,
  strings,
}: {
  text: string;
  summary: Summary;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const summaryText = summary.selected
    .map((sentence) =>
      collapseWhitespace(text.slice(sentence.start, sentence.end))
    )
    .join(" ");
  const summaryWords = countWords(summaryText);
  const share = summaryWords / Math.max(1, countWords(text));
  const matches = summary.selected.map((sentence) => ({
    start: sentence.start,
    end: sentence.end,
    category: "kept",
  }));
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {summary.truncated ? (
        <Notice>
          {t(strings.truncated, { count: fmt(MAX_SUMMARY_SENTENCES) })}
        </Notice>
      ) : null}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.statSentences}
          value={`${fmt(summary.selected.length)} / ${fmt(summary.candidates)}`}
        />
        <StatCard label={strings.statWords} value={fmt(summaryWords)} />
        <StatCard
          label={strings.statShare}
          value={fmt(share, { style: "percent", maximumFractionDigits: 0 })}
        />
      </StatGrid>
      <div className={RESULT_CARD}>
        <p className="text-foreground leading-relaxed">{summaryText}</p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copySummary}
        text={summaryText}
      />
      <p className={HINT}>{strings.honesty}</p>
      <Keywords strings={strings} summary={summary} />
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.highlightHeading}</h3>
        <HighlightedText
          label={strings.highlightLabel}
          matches={matches}
          text={text}
          tones={{ kept: "emerald" }}
        />
      </div>
      <Ranking strings={strings} summary={summary} text={text} />
    </section>
  );
}

function Controls({
  settings,
  onChange,
  strings,
}: {
  settings: Settings;
  onChange: (next: Settings) => void;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const set = (patch: Partial<Settings>) => onChange({ ...settings, ...patch });
  const bySentences = settings.mode === "sentences";
  const sliderLabel = bySentences
    ? tPlural(strings.sentencesSlider, locale, settings.sentences, {
        count: fmt(settings.sentences),
      })
    : t(strings.percentSlider, {
        percent: fmt(settings.percent / 100, { style: "percent" }),
      });
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <PillGroup
        label={strings.methodLabel}
        name="sum-method"
        onChange={(method) => set({ method })}
        options={[
          { value: "textrank", label: strings.methodTextrank },
          { value: "frequency", label: strings.methodFrequency },
        ]}
        value={settings.method}
      />
      <PillGroup
        label={strings.lengthLabel}
        name="sum-mode"
        onChange={(mode) => set({ mode })}
        options={[
          { value: "sentences", label: strings.bySentences },
          { value: "percent", label: strings.byPercent },
        ]}
        value={settings.mode}
      />
      <div className="space-y-2">
        <label className={LABEL} htmlFor="sum-length">
          {sliderLabel}
        </label>
        <input
          className="w-full accent-primary"
          id="sum-length"
          max={bySentences ? 15 : 60}
          min={bySentences ? 1 : 5}
          onChange={(event) => {
            const value = Number(event.target.value);
            set(bySentences ? { sentences: value } : { percent: value });
          }}
          step={bySentences ? 1 : 5}
          type="range"
          value={bySentences ? settings.sentences : settings.percent}
        />
      </div>
      <div className="flex items-end">
        <Checkbox
          checked={settings.leadBias}
          label={strings.leadBias}
          onChange={(leadBias) => set({ leadBias })}
        />
      </div>
    </div>
  );
}

export default function TextSummarizerWidget() {
  const strings = useToolUi(TEXT_SUMMARIZER_STRINGS);
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [settings, setSettings] = useState<Settings>({
    method: "textrank",
    mode: "sentences",
    sentences: 3,
    percent: 20,
    leadBias: true,
  });
  const summary = useMemo(() => {
    if (!submitted.trim()) {
      return null;
    }
    return summarize(submitted, {
      method: settings.method,
      length:
        settings.mode === "sentences"
          ? { by: "sentences", value: settings.sentences }
          : { by: "percent", value: settings.percent },
      leadBias: settings.leadBias,
    });
  }, [submitted, settings]);

  function body() {
    if (!summary) {
      return <Notice>{strings.emptyPrompt}</Notice>;
    }
    if (summary.candidates < MIN_SENTENCES) {
      return <Notice>{strings.tooShort}</Notice>;
    }
    return <Results strings={strings} summary={summary} text={submitted} />;
  }

  return (
    <>
      <TextAreaField
        id="sum-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={12}
        value={text}
      />
      <Controls onChange={setSettings} settings={settings} strings={strings} />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_PRIMARY}
          disabled={text.trim().length === 0}
          onClick={() => setSubmitted(text)}
          type="button"
        >
          {strings.summarize}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setText(strings.sample);
            setSubmitted(strings.sample);
          }}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => {
            setText("");
            setSubmitted("");
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {summary && submitted !== text ? (
        <p className={HINT}>{strings.changed}</p>
      ) : null}
      {body()}
    </>
  );
}
